import React, { useMemo } from 'react'
import { Flex, LineChart, Loading, Text, useAppTheme } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { ListingDetailViewsReferencePopover } from './ListingDetailViewsReferencePopover'
import { AccordionCard } from 'components/AccordionCard'
import { useShiftDetailContextValue } from '../../context/ShiftDetailProvider'
import { useTierEligibilitiesByShiftIdQuery } from 'queries/shifts/useTierEligibilitiesByShiftIdQuery'
import { useListingViewsQuery } from 'queries/listings/useListingViewsQuery'
import { formatInTimeZone } from 'date-fns-tz'
import { getDefaultListingFunnelDateRange } from '../ListingsOverview/components/ListingFunnelPanel/utils/getDefaultListingFunnelDateRange'
import { format } from 'date-fns'

export const TotalListingViews = () => {
  const { colors } = useAppTheme()

  const {
    state: {
      id: shiftId,
      createdAt,
      endsAt,
      listings,
      location: {
        address: { timezone },
      },
    },
  } = useShiftDetailContextValue()

  const { data: tierEligibilitiesData } =
    useTierEligibilitiesByShiftIdQuery(shiftId)

  const listingId = tierEligibilitiesData?.listingId ?? listings?.[0]?.id

  const { from, to } = getDefaultListingFunnelDateRange(createdAt, endsAt)
  const timeRangeParams = {
    startDate: format(from, 'yyyy-MM-dd'),
    endDate: format(to, 'yyyy-MM-dd'),
  }

  const { data, isLoading, isError, error } = useListingViewsQuery(
    listingId,
    timeRangeParams
  )

  const parsedError = parseErrorMessage(error)

  const chartData = useMemo(() => {
    let organicTotal = 0
    let deeplinkTotal = 0

    return (data?.listingViews ?? []).map(
      ({ date, organicViews, deeplinkViews }) => {
        organicTotal += organicViews
        deeplinkTotal += deeplinkViews

        const isCurrentYear =
          new Date(date).getFullYear() === new Date().getFullYear()
        return {
          date: formatInTimeZone(
            date,
            timezone,
            isCurrentYear ? 'EEE, M/d' : 'EEE, M/d/yy'
          ),
          organicViews: organicTotal,
          deeplinkViews: deeplinkTotal,
        }
      }
    )
  }, [data?.listingViews, timezone])

  if (!listingId) return null

  return (
    <AccordionCard
      title={
        <Flex
          justifyContent="space-between"
          alignItems="baseline"
          width="100%"
          gap={2}
        >
          <Text fontSize={2} color="lightText">
            Listing Detail Views <ListingDetailViewsReferencePopover />
          </Text>
          {data?.dataRefreshedAt ? (
            <Text fontSize={1} color="lightText">
              Last refreshed at{' '}
              {formatInTimeZone(
                new Date(data.dataRefreshedAt),
                timezone,
                'MMM d, h:mm a z'
              )}
            </Text>
          ) : null}
        </Flex>
      }
      size="fit"
      // Intentionally disabled. This should work, unsure why it's throwing an error
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      style={{
        flex: '1 1 auto',
        whiteSpace: 'nowrap',
        minWidth: '400px',
        maxWidth: '592px',
      }}
    >
      {isLoading ? (
        <Flex minHeight={208} justifyContent="center" alignItems="center">
          <Loading />
        </Flex>
      ) : isError ? (
        <Text color={colors.errors[200]} fontSize={2}>
          {parsedError
            ? parsedError
            : 'Failed to load listing views. Please try again or contact eng support.'}
        </Text>
      ) : (
        <LineChart
          data={chartData}
          curveType="bump"
          dataKey="date"
          height="208px"
          series={[
            {
              name: 'organicViews',
              color: colors.primary300,
              label: 'Organic',
            },
            {
              name: 'deeplinkViews',
              color: colors.primary200,
              label: 'Deeplink',
            },
          ]}
        />
      )}
    </AccordionCard>
  )
}
